import type {
  AssigneeSummary,
  ConversationListItemResponse,
  CustomerSentiment,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import {
  ConversationAssigneeSummary,
  ConversationResolutionSentiment,
} from "@redotech/redo-model/conversation-insights";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  ConversationAssigneeSummaryColorMap,
  ConversationAssigneeSummaryLabels,
  ConversationResolutionSentimentColorMap,
  ConversationResolutionSentimentLabels,
} from "@redotech/redo-web/conversation-insights/conversation-insights-ui";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { ConversationDetailsSection } from "./conversation-details-section";
import { ConversationTicketFieldTitle } from "./conversation-ticket-field-title";

export interface ConversationSummarySectionProps {
  conversation: ConversationListItemResponse;
}

const SentimentBadge = memo(function SentimentBadge({
  sentiment,
}: {
  sentiment: CustomerSentiment;
}) {
  const sentimentEnum = sentiment as unknown as ConversationResolutionSentiment;
  return (
    <RedoBadge
      color={ConversationResolutionSentimentColorMap[sentimentEnum]}
      size="sm"
      text={ConversationResolutionSentimentLabels[sentimentEnum]}
    />
  );
});

const AssigneeSummaryBadge = memo(function AssigneeSummaryBadge({
  assigneeSummary,
}: {
  assigneeSummary: AssigneeSummary;
}) {
  const assigneeSummaryEnum =
    assigneeSummary as unknown as ConversationAssigneeSummary;
  return (
    <RedoBadge
      color={ConversationAssigneeSummaryColorMap[assigneeSummaryEnum]}
      size="sm"
      text={ConversationAssigneeSummaryLabels[assigneeSummaryEnum]}
    />
  );
});

const formatTimestamp = (date: Date | null): string => {
  if (!date) {
    return "-";
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const ConversationSummarySection = memo(
  function ConversationSummarySection({
    conversation,
  }: ConversationSummarySectionProps) {
    const hasSummaryData =
      conversation.summary ||
      conversation.customerSentiment ||
      conversation.assigneeSummary;

    if (!hasSummaryData) {
      return null;
    }

    return (
      <ConversationDetailsSection
        localStorageKey="redo.support-v2.conversation-details.summary.collapsed"
        title="Summary"
      >
        {conversation.summary && (
          <Flex dir="column" gap="xs">
            <ConversationTicketFieldTitle>
              Conversation Summary
            </ConversationTicketFieldTitle>
            <Text fontSize="xs" textColor="tertiary">
              {conversation.summary}
            </Text>
          </Flex>
        )}

        {conversation.customerSentiment && (
          <Flex align="center" gap="md" justify="space-between">
            <ConversationTicketFieldTitle>
              Customer Sentiment
            </ConversationTicketFieldTitle>
            <SentimentBadge sentiment={conversation.customerSentiment} />
          </Flex>
        )}

        {conversation.assigneeSummary && (
          <Flex align="center" gap="md" justify="space-between">
            <ConversationTicketFieldTitle>
              Assignee Summary
            </ConversationTicketFieldTitle>
            <AssigneeSummaryBadge
              assigneeSummary={conversation.assigneeSummary}
            />
          </Flex>
        )}

        <Flex align="center" gap="md" justify="space-between">
          <ConversationTicketFieldTitle>
            Last Customer Message
          </ConversationTicketFieldTitle>
          <Text fontSize="xs" textColor="tertiary">
            {formatTimestamp(conversation.lastCustomerMessageAt)}
          </Text>
        </Flex>

        <Flex align="center" gap="md" justify="space-between">
          <ConversationTicketFieldTitle>
            Last Merchant Message
          </ConversationTicketFieldTitle>
          <Text fontSize="xs" textColor="tertiary">
            {formatTimestamp(conversation.lastMerchantMessageAt)}
          </Text>
        </Flex>
      </ConversationDetailsSection>
    );
  },
);
